/**
 * Builds a canonical course URL in the format `/c/<slug>_<courseCode>`.
 * The entire path is lowercased for SEO consistency.
 *
 * When a productCode is provided, appends `?sku=<productCode>` so the course page
 * preselects that edition. The query string keeps its original casing.
 *
 * @param slug - The SEO-friendly slug portion of the URL.
 * @param courseCode - The unique course code used for data fetching.
 * @param productCode - Optional edition product code to preselect.
 * @returns The canonical course URL path (path fully lowercased).
 */
export function buildCourseUrl(slug: string, courseCode: string, productCode?: string | null): string {
	const path = `/c/${slug}_${courseCode}`.toLowerCase();
	return productCode ? `${path}?sku=${encodeURIComponent(productCode)}` : path;
}

/**
 * Parses a combined course URL parameter in the format `<slug>_<courseCode>`.
 * The course code is extracted from the portion after the last underscore
 * and uppercased to match DatoCMS storage format.
 * The slug is the portion before the last underscore (may itself contain underscores).
 *
 * @param param - The combined URL parameter (e.g. `my-course-slug_abc123`).
 * @returns An object with the parsed `slug` and uppercased `courseCode`.
 * @throws {Error} If the parameter does not contain an underscore separator.
 */
export function parseCourseParam(param: string): { slug: string; courseCode: string } {
	const lastUnderscoreIndex = param.lastIndexOf('_');

	if (lastUnderscoreIndex === -1) {
		throw new Error(`Invalid course parameter: "${param}". Expected format: <slug>_<courseCode>`);
	}

	return {
		slug: param.slice(0, lastUnderscoreIndex),
		courseCode: param.slice(lastUnderscoreIndex + 1).toUpperCase(),
	};
}

/**
 * Asserts that the slug portion of the URL matches the actual course slug from DatoCMS.
 * Comparison is case-insensitive since `buildCourseUrl` lowercases the entire path.
 *
 * @param urlSlug - The slug extracted from the URL parameter.
 * @param actualSlug - The canonical slug stored in DatoCMS.
 * @returns `true` if the slugs match (case-insensitive).
 */
export function isCourseSlugMatch(urlSlug: string, actualSlug: string): boolean {
	return urlSlug.toLowerCase() === actualSlug.toLowerCase();
}
